// ESC/POS thermal printing from the browser via Web Serial (USB-serial / Bluetooth SPP)
// or WebUSB. Requires Chrome/Edge on https or localhost.
import { AWS_URL } from "../../../services/http.service";
const enc = new TextEncoder();
const ESC = 0x1b;
const GS = 0x1d;
const CMD = {
  init: [ESC, 0x40],
  left: [ESC, 0x61, 0],
  center: [ESC, 0x61, 1],
  boldOn: [ESC, 0x45, 1],
  boldOff: [ESC, 0x45, 0],
  cut: [GS, 0x56, 0x42, 0x03],
};

const COLS = Number(localStorage.getItem("printerCols")) || 32; // 58mm=32, 80mm=48
let device = null; // { type: "serial", port } | { type: "usb", dev, ep }

export const isPrinterSupported = () =>
  typeof navigator !== "undefined" && ("serial" in navigator || "usb" in navigator);

const openSerial = async (port) => {
  if (!port.readable && !port.writable) await port.open({ baudRate: 9600 });
  device = { type: "serial", port };
};

const openUsb = async (dev) => {
  if (!dev.opened) await dev.open();
  try {
    if (dev.configuration === null) {
      await dev.selectConfiguration(dev.configurations[0]?.configurationValue || 1);
    }
    // Prefer the printer class (7) interface, then any interface with an OUT endpoint.
    const candidates = [];
    for (const iface of dev.configuration.interfaces) {
      for (const alt of iface.alternates) {
        const out = alt.endpoints.find(
          (e) => e.direction === "out" && e.type !== "isochronous",
        );
        if (out) {
          candidates.push({
            num: iface.interfaceNumber,
            alt: alt.alternateSetting,
            ep: out.endpointNumber,
            isPrinter: alt.interfaceClass === 7,
          });
        }
      }
    }
    candidates.sort((a, b) => Number(b.isPrinter) - Number(a.isPrinter));
    if (!candidates.length) throw new Error("No writable USB endpoint found");

    let lastErr = null;
    for (const c of candidates) {
      try {
        if (!dev.configuration.interfaces.find((i) => i.interfaceNumber === c.num)?.claimed) {
          await dev.claimInterface(c.num);
        }
        if (c.alt) await dev.selectAlternateSetting(c.num, c.alt);
        device = { type: "usb", dev, ep: c.ep };
        return;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr;
  } catch (e) {
    try {
      await dev.close();
    } catch (_) {
      /* ignore */
    }
    const msg = String(e?.message || e);
    if (/claim|access denied|busy/i.test(msg)) {
      throw new Error(
        "USB printer is in use by the Windows printer driver. Use 'Bluetooth/COM' if the printer exposes a COM port, or install the WinUSB driver for it (Zadig). Close other apps using the printer and retry.",
      );
    }
    throw e;
  }
};

// Must be called from a user gesture (button click). Permission is remembered.
export const connectPrinter = async (kind = "serial") => {
  try {
    if (kind === "usb") {
      if (!("usb" in navigator)) throw new Error("WebUSB is not supported in this browser");
      device = null;
      await openUsb(await navigator.usb.requestDevice({ filters: [] }));
      localStorage.setItem("printerKind", "usb");
    } else {
      if (!("serial" in navigator)) throw new Error("Web Serial is not supported in this browser");
      await openSerial(await navigator.serial.requestPort());
      localStorage.setItem("printerKind", "serial");
    }
  } catch (e) {
    if (e?.name === "NotFoundError") throw new Error("No printer selected");
    throw e;
  }
};

// Reconnect silently to a previously authorised printer.
const autoConnect = async () => {
  if (device) return true;
  const preferUsb = localStorage.getItem("printerKind") === "usb";
  const tryUsb = async () => {
    if (!("usb" in navigator)) return false;
    const [dev] = await navigator.usb.getDevices();
    if (!dev) return false;
    await openUsb(dev);
    return true;
  };
  const trySerial = async () => {
    if (!("serial" in navigator)) return false;
    const [port] = await navigator.serial.getPorts();
    if (!port) return false;
    await openSerial(port);
    return true;
  };
  for (const fn of preferUsb ? [tryUsb, trySerial] : [trySerial, tryUsb]) {
    try {
      if (await fn()) return true;
    } catch (e) {
      device = null;
    }
  }
  return false;
};


export const isPrinterConnected = () => !!device;

const write = async (bytes) => {
  const data = new Uint8Array(bytes);
  if (device.type === "serial") {
    const w = device.port.writable.getWriter();
    try {
      await w.write(data);
    } finally {
      w.releaseLock();
    }
  } else {
    for (let i = 0; i < data.length; i += 512) {
      await device.dev.transferOut(device.ep, data.slice(i, i + 512));
    }
  }
};

const clean = (s) => String(s ?? "").replace(/[^\x20-\x7E]/g, "?");
const money = (n) => (Number(n) || 0).toFixed(2);
const lr = (l, r) => {
  l = clean(l);
  r = clean(r);
  const gap = Math.max(1, COLS - l.length - r.length);
  return l.slice(0, COLS - r.length - 1) + " ".repeat(gap) + r;
};
const line = () => "-".repeat(COLS);

const PX_WIDTH = COLS >= 48 ? 576 : 384;

// Fetch an image and convert it to an ESC/POS raster (GS v 0) block.
const imageToRaster = async (url, maxPx) => {
  try {
    const res = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob());
    const scale = Math.min(1, maxPx / bmp.width);
    const w = Math.max(8, Math.floor((bmp.width * scale) / 8) * 8);
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data;
    const bytesPerRow = w / 8;
    const out = [GS, 0x76, 0x30, 0, bytesPerRow & 0xff, bytesPerRow >> 8, h & 0xff, h >> 8];
    for (let y = 0; y < h; y++) {
      for (let bx = 0; bx < bytesPerRow; bx++) {
        let b = 0;
        for (let bit = 0; bit < 8; bit++) {
          const i = (y * w + bx * 8 + bit) * 4;
          const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
          if (lum < 128) b |= 0x80 >> bit;
        }
        out.push(b);
      }
    }
    return out;
  } catch (e) {
    return null;
  }
};

const buildReceipt = async ({ businessName, business, sale, items, taxes, values }) => {
  const out = [...CMD.init, ...CMD.center];
  const t = (s) => out.push(...enc.encode(clean(s) + "\n"));
  const wrap = (s) => {
    const words = clean(s).split(/\s+/);
    let cur = "";
    words.forEach((w) => {
      if ((cur + " " + w).trim().length > COLS) {
        if (cur) t(cur);
        cur = w;
      } else cur = (cur + " " + w).trim();
    });
    if (cur) t(cur);
  };

  if (business?.businesslogo) {
    const logo = await imageToRaster(AWS_URL + business.businesslogo, Math.min(PX_WIDTH, 192));
    if (logo) out.push(...logo, 0x0a);
  }
  out.push(...CMD.boldOn);
  t(businessName || business?.businessName || "Sale Receipt");
  out.push(...CMD.boldOff);
  if (business?.address) wrap(business.address);
  if (business?.phone) t(`Ph: ${business.phone}`);
  if (business?.gstNumber) t(`GSTIN: ${business.gstNumber}`);
  t(line());
  out.push(...CMD.left);
  const inv = sale?.invoiceId || sale?.invoiceNumber || sale?.invoiceNo || sale?.sellUuid;
  if (inv) t(`Invoice: ${inv}`);
  t(`Date: ${new Date().toLocaleString("en-IN")}`);
  if (values?.customerName) t(`Customer: ${values.customerName}`);
  if (values?.mobile || sale?.mobile) t(`Mobile: ${values?.mobile || sale?.mobile}`);
  t(line());

  let total = 0;
  let cgstAmt = 0;
  let sgstAmt = 0;
  let igstAmt = 0;
  let cessAmt = 0;
  items.forEach((it) => {
    const qty = Number(it.quantity) || 1;
    const amt = Number(it.finalPrice) || Number(it.salePrice) * qty || 0;
    total += amt;
    t(it.productName || it.name);
    t(lr(`  ${qty} x ${money(it.salePrice)}`, money(amt)));
  });
  const hasTaxTotals =
    taxes && [taxes.cgst, taxes.sgst, taxes.igst, taxes.cess].some((v) => Number(v) > 0);
  if (hasTaxTotals) {
    cgstAmt = Number(taxes.cgst) || 0;
    sgstAmt = Number(taxes.sgst) || 0;
    igstAmt = Number(taxes.igst) || 0;
    cessAmt = Number(taxes.cess) || 0;
  }
  t(line());
  if (hasTaxTotals) {
    if (cgstAmt) t(lr("CGST", money(cgstAmt)));
    if (sgstAmt) t(lr("SGST", money(sgstAmt)));
    if (igstAmt) t(lr("IGST", money(igstAmt)));
    if (cessAmt) t(lr("CESS", money(cessAmt)));
    t(line());
  }
  const grand = Number(values?.transactionAmount ?? sale?.transactionAmount) || total;
  out.push(...CMD.boldOn);
  t(lr("TOTAL", money(grand)));
  out.push(...CMD.boldOff, ...CMD.center);

  if (business?.aadharPhoto) {
    const qr = await imageToRaster(AWS_URL + business.aadharPhoto, Math.min(PX_WIDTH, 200));
    if (qr) {
      t("");
      out.push(...qr, 0x0a);
      t("Scan to pay");
    }
  }
  t("\nThank you! Visit again\n\n\n");
  out.push(...CMD.cut);
  return out;
};

// Never throws: printing must not break sale creation.
export const printSaleReceipt = async (payload) => {
  try {
    if (!isPrinterSupported() || !(await autoConnect())) return false;
    await write(await buildReceipt(payload));
    return true;
  } catch (e) {
    console.error("Thermal print failed:", e);
    device = null;
    return false;
  }
};

